import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronsUpDown, Check } from 'lucide-react';
import { SidebarHeader, SidebarTrigger, useSidebar } from '@shared/ui/sidebar';
import { SuiteIcon } from '@shared/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { allSuites } from '@suites/registry';
import { useActiveSuite } from '../useActiveSuite';
import { cn } from '@shared/lib/utils';

export const SidebarSuiteHeader: React.FC = () => {
  const activeSuite = useActiveSuite();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  if (!activeSuite) return null;

  const displaySuites = allSuites;

  return (
    <SidebarHeader className="p-2 border-b border-sidebar-border">
      <div className="flex items-center justify-between gap-1 w-full min-w-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex items-center gap-2.5 p-1.5 rounded-lg text-left transition-colors hover:bg-sidebar-accent/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring cursor-pointer group min-w-0 flex-1',
                isCollapsed && 'justify-center p-1.5'
              )}
              title="Switch Suite"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                <SuiteIcon name={activeSuite.icon} className="h-4 w-4" />
              </div>
              {!isCollapsed && (
                <div className="flex items-center justify-between flex-1 min-w-0">
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold tracking-tight text-sidebar-foreground truncate">
                      {activeSuite.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate leading-tight">
                      Switch Suite
                    </span>
                  </div>
                  <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/70 ml-1.5 shrink-0" />
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" sideOffset={6} className="w-64 p-1 z-50">
            <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
              Switch Academic Suite
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            <div className="space-y-0.5">
              {displaySuites.map((suite) => {
                const isActive = suite.id === activeSuite.id;
                return (
                  <DropdownMenuItem asChild key={suite.id} className="p-0">
                    <Link
                      to={suite.basePath}
                      className={cn(
                        'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs transition-colors hover:bg-accent focus:bg-accent',
                        isActive && 'bg-accent font-semibold text-primary'
                      )}
                    >
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <SuiteIcon name={suite.icon} className="h-3.5 w-3.5" />
                      </div>
                      <span className="flex-1 truncate">{suite.name}</span>
                      {isActive && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        {!isCollapsed && (
          <SidebarTrigger className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0 ml-1" />
        )}
      </div>
    </SidebarHeader>
  );
};
